import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Wrench, DollarSign, Edit, Trash2, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import api from '../utils/api';

const STATUS_OPTIONS = ['pending', 'in_progress', 'completed'];

export default function RepairLog() {
  const [repairs, setRepairs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    stock_number: '', date: '', repair_type: '',
    parts_cost: '', labor_cost: '', mechanic: '',
    notes: '', status: 'completed'
  });

  const fetchData = useCallback(async () => {
    try {
      const [repRes, sumRes] = await Promise.all([
        api.get('/api/repairs'),
        api.get('/api/repairs/summary')
      ]);
      setRepairs(repRes.data);
      setSummary(sumRes.data);
    } catch { toast.error('Failed to load repairs'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      parts_cost: parseFloat(form.parts_cost) || 0,
      labor_cost: parseFloat(form.labor_cost) || 0
    };
    try {
      if (editing) {
        await api.put(`/api/repairs/${editing.id}`, payload);
        toast.success('Repair updated');
      } else {
        await api.post('/api/repairs', payload);
        toast.success('Repair logged');
      }
      resetForm();
      fetchData();
    } catch { toast.error('Failed to save repair'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this repair record?')) return;
    try {
      await api.delete(`/api/repairs/${id}`);
      toast.success('Repair deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const startEdit = (rep) => {
    setEditing(rep);
    setForm({
      stock_number: rep.stock_number || '', date: rep.date || '',
      repair_type: rep.repair_type || '', parts_cost: rep.parts_cost?.toString() || '',
      labor_cost: rep.labor_cost?.toString() || '', mechanic: rep.mechanic || '',
      notes: rep.notes || '', status: rep.status || 'completed'
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false); setEditing(null);
    setForm({ stock_number: '', date: '', repair_type: '', parts_cost: '', labor_cost: '', mechanic: '', notes: '', status: 'completed' });
  };

  const filtered = repairs.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return r.stock_number?.toLowerCase().includes(s) || r.repair_type?.toLowerCase().includes(s) || r.mechanic?.toLowerCase().includes(s) || r.notes?.toLowerCase().includes(s);
  });

  const statusColor = (s) => {
    const map = { completed: 'bg-green-100 text-green-700', in_progress: 'bg-blue-100 text-blue-700', pending: 'bg-amber-100 text-amber-700' };
    return map[s] || 'bg-gray-100 text-gray-700';
  };

  if (loading) return <div className="animate-pulse space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-gray-100 rounded-xl" />)}</div>;

  return (
    <div className="space-y-6" data-testid="repair-log">
      {/* Summary Cards */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="repair-total-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center">
              <Wrench className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm text-gray-500">Total Repairs</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{summary?.count || 0}</div>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm text-gray-500">Parts Cost</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">${(summary?.total_parts || 0).toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm text-gray-500">Labor Cost</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">${(summary?.total_labor || 0).toLocaleString()}</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search repairs..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-xl" data-testid="repair-search" />
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl" data-testid="add-repair-btn">
          <Plus className="w-4 h-4 mr-2" /> Log Repair
        </Button>
      </div>

      {/* Repair List */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center" data-testid="repair-empty">
          <Wrench className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No repair records</h3>
          <p className="text-gray-500 text-sm">Log vehicle repairs to track maintenance costs.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(rep => (
            <div key={rep.id} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-all" data-testid={`repair-card-${rep.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-mono text-sm font-semibold text-gray-900 bg-gray-100 px-2 py-0.5 rounded-lg">{rep.stock_number || 'N/A'}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(rep.status)}`}>
                      {rep.status === 'in_progress' ? 'In Progress' : rep.status?.charAt(0).toUpperCase() + rep.status?.slice(1)}
                    </span>
                    {rep.date && <span className="text-xs text-gray-400">{rep.date}</span>}
                  </div>
                  <p className="text-gray-700 font-medium mb-1">{rep.repair_type || 'No description'}</p>
                  {rep.mechanic && <p className="text-sm text-gray-500">Mechanic: {rep.mechanic}</p>}
                  {rep.notes && <p className="text-sm text-gray-400 mt-1">{rep.notes}</p>}
                  <div className="flex items-center gap-4 mt-3 text-sm">
                    <span className="text-gray-500">Parts: <span className="font-semibold text-gray-900">${(rep.parts_cost || 0).toFixed(2)}</span></span>
                    <span className="text-gray-500">Labor: <span className="font-semibold text-gray-900">${(rep.labor_cost || 0).toFixed(2)}</span></span>
                    <span className="text-gray-500">Total: <span className="font-bold text-orange-600">${(rep.total_cost || 0).toFixed(2)}</span></span>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-4">
                  <button onClick={() => startEdit(rep)} className="p-2 rounded-lg hover:bg-blue-50 text-blue-600" data-testid={`edit-repair-${rep.id}`}><Edit className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(rep.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" data-testid={`delete-repair-${rep.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={v => { if (!v) resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{editing ? 'Edit Repair' : 'Log Repair'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 text-sm font-medium">Vehicle Stock # *</Label>
                <Input value={form.stock_number} onChange={e => setForm({...form, stock_number: e.target.value})} placeholder="e.g. WAS0326012" className="rounded-xl" required data-testid="repair-form-stock" />
              </div>
              <div>
                <Label className="text-gray-700 text-sm font-medium">Date</Label>
                <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="rounded-xl" data-testid="repair-form-date" />
              </div>
            </div>
            <div>
              <Label className="text-gray-700 text-sm font-medium">Repair Description *</Label>
              <Textarea value={form.repair_type} onChange={e => setForm({...form, repair_type: e.target.value})} placeholder="e.g. oil change/filter, tune up, battery replaced..." rows={3} className="rounded-xl" required data-testid="repair-form-type" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 text-sm font-medium">Parts Cost</Label>
                <Input type="number" step="0.01" value={form.parts_cost} onChange={e => setForm({...form, parts_cost: e.target.value})} placeholder="0.00" className="rounded-xl" data-testid="repair-form-parts" />
              </div>
              <div>
                <Label className="text-gray-700 text-sm font-medium">Labor Cost</Label>
                <Input type="number" step="0.01" value={form.labor_cost} onChange={e => setForm({...form, labor_cost: e.target.value})} placeholder="0.00" className="rounded-xl" data-testid="repair-form-labor" />
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl px-4 py-3">
              <span className="text-sm text-gray-500">Total Cost: </span>
              <span className="text-lg font-bold text-orange-600">${((parseFloat(form.parts_cost) || 0) + (parseFloat(form.labor_cost) || 0)).toFixed(2)}</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 text-sm font-medium">Mechanic</Label>
                <Input value={form.mechanic} onChange={e => setForm({...form, mechanic: e.target.value})} placeholder="Mechanic name" className="rounded-xl" data-testid="repair-form-mechanic" />
              </div>
              <div>
                <Label className="text-gray-700 text-sm font-medium">Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger className="rounded-xl" data-testid="repair-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-gray-700 text-sm font-medium">Notes</Label>
              <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Additional details..." className="rounded-xl" data-testid="repair-form-notes" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl" data-testid="repair-form-submit">
                {editing ? 'Update' : 'Log Repair'}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm} className="rounded-xl">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
